"use client";

import { useCallback, useEffect, useState } from "react";

import { Check, Cloud, CloudOff, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { checkPatientAlgoliaSyncStatus, syncPatientTimelineToAlgolia } from "@/app/actions/search-actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface AlgoliaSyncButtonProps {
  patientId: string;
  patientName?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  showLabel?: boolean;
  /** Auto-sync on mount if not already synced */
  autoSync?: boolean;
  /** Callback when sync completes */
  onSyncComplete?: (success: boolean) => void;
  /** Show as inline status badge instead of button */
  inline?: boolean;
}

interface SyncStatus {
  isSynced: boolean;
  lastSyncedAt: string | null;
  indexName: string | null;
}

export function AlgoliaSyncButton({
  patientId,
  patientName,
  variant = "outline",
  size = "sm",
  showLabel = true,
  autoSync = true,
  onSyncComplete,
  inline = false,
}: AlgoliaSyncButtonProps) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [lastSyncResult, setLastSyncResult] = useState<"idle" | "success" | "error">("idle");

  // Check sync status on mount using server action
  const checkSyncStatus = useCallback(async () => {
    setIsChecking(true);
    try {
      const result = await checkPatientAlgoliaSyncStatus(patientId);

      if (result.success) {
        setSyncStatus({
          isSynced: result.isSynced || false,
          lastSyncedAt: result.lastSyncedAt || null,
          indexName: result.indexName || null,
        });
        return result.isSynced || false;
      }
    } catch (error) {
      console.error("[Algolia] Failed to check sync status:", error);
    } finally {
      setIsChecking(false);
    }
    return false;
  }, [patientId]);

  // Sync function using server action
  const handleSync = useCallback(
    async (force = false) => {
      if (isSyncing) return;

      setIsSyncing(true);
      setLastSyncResult("idle");

      try {
        const result = await syncPatientTimelineToAlgolia({
          patientId,
          syncTimeline: true,
          syncPatient: true,
          force,
        });

        if (result.success) {
          setLastSyncResult("success");
          setSyncStatus({
            isSynced: true,
            lastSyncedAt: result.lastSyncedAt || new Date().toISOString(),
            indexName: result.indexName || result.results?.indexName || null,
          });

          // Only show toast for manual syncs (force=true) or first syncs
          if (force || !result.alreadySynced) {
            toast.success("Timeline synced to Algolia", {
              description: result.alreadySynced
                ? "Already synced - no changes needed"
                : `${result.results?.timelineIndexed || 0} events indexed for fast search`,
            });
          }

          onSyncComplete?.(true);
        } else {
          setLastSyncResult("error");
          toast.error("Sync failed", {
            description: result.error || "Failed to sync timeline to Algolia",
          });
          onSyncComplete?.(false);
        }
      } catch (error: any) {
        setLastSyncResult("error");
        toast.error("Sync error", {
          description: error.message || "An unexpected error occurred",
        });
        onSyncComplete?.(false);
      } finally {
        setIsSyncing(false);
        // Reset visual status after 3 seconds
        setTimeout(() => setLastSyncResult("idle"), 3000);
      }
    },
    [patientId, isSyncing, onSyncComplete]
  );

  // Auto-check and auto-sync on mount
  useEffect(() => {
    const initSync = async () => {
      const isSynced = await checkSyncStatus();
      if (!isSynced && autoSync) {
        // Auto-sync silently if not synced
        handleSync(false);
      }
    };
    initSync();
  }, [checkSyncStatus, autoSync, handleSync]);

  // Format last synced date
  const formatLastSynced = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Inline status badge variant
  if (inline) {
    if (isChecking) {
      return (
        <Badge variant="outline" className="gap-1.5 text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking...
        </Badge>
      );
    }

    if (syncStatus?.isSynced) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="cursor-pointer gap-1.5 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400"
                onClick={() => handleSync(true)}
              >
                <Check className="h-3 w-3" />
                Synced {formatLastSynced(syncStatus.lastSyncedAt)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Click to force re-sync to Algolia</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Badge
        variant="outline"
        className={cn(
          "cursor-pointer gap-1.5",
          isSyncing
            ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400"
            : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400"
        )}
        onClick={() => !isSyncing && handleSync(false)}
      >
        {isSyncing ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" />
            Syncing...
          </>
        ) : (
          <>
            <CloudOff className="h-3 w-3" />
            Not Synced
          </>
        )}
      </Badge>
    );
  }

  // Determine icon and state
  const getIcon = () => {
    if (isChecking || isSyncing) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    if (lastSyncResult === "success") {
      return <Check className="h-4 w-4 text-green-500" />;
    }
    if (lastSyncResult === "error") {
      return <CloudOff className="h-4 w-4 text-red-500" />;
    }
    if (syncStatus?.isSynced) {
      return <RefreshCw className="h-4 w-4" />;
    }
    return <Cloud className="h-4 w-4" />;
  };

  const getLabel = () => {
    if (isChecking) return "Checking...";
    if (isSyncing) return "Syncing...";
    if (lastSyncResult === "success") return "Synced!";
    if (lastSyncResult === "error") return "Failed";
    if (syncStatus?.isSynced) return `Re-sync (${formatLastSynced(syncStatus.lastSyncedAt)})`;
    return "Sync to Algolia";
  };

  const buttonContent = (
    <>
      {getIcon()}
      {showLabel && <span className="ml-1.5">{getLabel()}</span>}
    </>
  );

  const handleButtonClick = () => {
    // Force re-sync if already synced, normal sync if not
    handleSync(syncStatus?.isSynced || false);
  };

  if (!showLabel) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={variant}
              size={size}
              onClick={handleButtonClick}
              disabled={isChecking || isSyncing}
              className={cn(
                "gap-1",
                syncStatus?.isSynced &&
                  lastSyncResult === "idle" &&
                  "border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400"
              )}
            >
              {buttonContent}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {syncStatus?.isSynced
                ? `Last synced ${formatLastSynced(syncStatus.lastSyncedAt)} • Click to re-sync`
                : "Sync timeline to Algolia for fast searching"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleButtonClick}
      disabled={isChecking || isSyncing}
      className={cn(
        "gap-1",
        syncStatus?.isSynced &&
          lastSyncResult === "idle" &&
          "border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400"
      )}
    >
      {buttonContent}
    </Button>
  );
}
